// src/components/vendor/ProductSearch.tsx

import React, { useState } from 'react';
import { Search, Plus, Package, X, AlertCircle, PackageOpen } from 'lucide-react';
import type { Product } from '../../../types';
import { formatPrice } from '../../../utils/utilityFunctions';
import { getProductImageUrl } from '../../../utils/productUtilities';

interface ProductSearchProps {
    onSelect: (product: Product) => void;
    onRemove?: (productId: number) => void;
    selectedProducts?: Product[];
    products?: Product[];
    placeholder?: string;
    disabled?: boolean;
    showResults?: boolean;
    loading?: boolean;
    maxHeight?: string;
    excludeSelected?: boolean;
    emptyMessage?: string;
    noResultsMessage?: string;
    hasFocused?: boolean; // New prop to indicate if the input has been focused
    hasLoaded?: boolean; // New prop to indicate if products have been loaded
    searchQuery?: string; // New prop to allow external control of the search query
    setSearchQuery?: (query: string) => void; // New prop to allow external control of the search query
    onFocus?: () => void; // New prop to handle focus event
    onClearSearch?: () => void; // New prop to handle clearing the search
    wrapperRef?: React.RefObject<HTMLDivElement>; // New prop to allow external control of the wrapper ref
}

const ProductSearch: React.FC<ProductSearchProps> = ({
    onSelect,
    onRemove,
    selectedProducts = [],
    placeholder = 'Search by product name or SKU...',
    disabled = false,
    showResults = false,
    maxHeight = 'max-h-48',
    excludeSelected = true,
    emptyMessage = 'You don\'t have any approved products yet. Add products to get started.',
    noResultsMessage = 'No products found matching your search.',
    hasLoaded = false,
    hasFocused = false,
    searchQuery = '',
    loading = false,
    setSearchQuery = () => {},
    onFocus = () => {},
    onClearSearch = () => {},
    wrapperRef,
    products = [],
}) => {

    const handleSelect = (product: Product) => {
        onSelect(product);
        setSearchQuery('');
    };

    const selectedIds = selectedProducts.map(p => p.id);
    const filteredProducts = excludeSelected
        ? products.filter(p => !selectedIds.includes(p.id))
        : products;
        
    // Determine what to show
    const showEmptyState = hasLoaded && !loading && products.length === 0 && !searchQuery.trim();
    const showNoResults = hasLoaded && !loading && products.length === 0 && searchQuery.trim();
    const showResultsList = hasLoaded && !loading && filteredProducts.length > 0 && hasFocused;
    
    return (
        <div ref={wrapperRef} className="relative">
            {/* Search Input */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {loading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                )}
                {(searchQuery && !loading) || showResults && (
                    <button
                        type="button"
                        onClick={onClearSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                    >
                        <X className="h-4 w-4 text-gray-400" />
                    </button>
                )}
            </div>

            {/* Results Dropdown */}
            {showResults && (
                <div className={`absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto ${maxHeight}`}>
                    {/* Empty State - No approved products */}
                    {showEmptyState && (
                        <div className="px-4 py-6 text-center">
                            <PackageOpen className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">{emptyMessage}</p>
                            <button
                                type="button"
                                className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                                onClick={() => {
                                    // Navigate to add product
                                    window.location.href = '/vendor/products';
                                }}
                            >
                                + Add Your First Product
                            </button>
                        </div>
                    )}

                    {/* No Results - Search found nothing */}
                    {showNoResults && (
                        <div className="px-4 py-6 text-center">
                            <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">{noResultsMessage}</p>
                            <p className="text-xs text-gray-400 mt-1">
                                Try adjusting your search or <button
                                    type="button"
                                    onClick={onClearSearch}
                                    className="text-blue-600 hover:underline"
                                >
                                    clear the search
                                </button>
                            </p>
                        </div>
                    )}

                    {/* Results List */}
                    {showResultsList && (
                        <>
                            {/* Optional: Show count */}
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
                                {filteredProducts.length} product{filteredProducts.length > 1 ? 's' : ''} found
                            </div>
                            {filteredProducts.map((product) => {
                                // Get primary image or first image
                                const primaryImage = product.images?.find(img => img.is_primary);
                                const displayImage = primaryImage || product.images?.[0];

                                return (
                                    <button
                                        key={product.id}
                                        onClick={() => handleSelect(product)}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b last:border-b-0 flex items-center gap-3"
                                    >
                                        {/* Product Image */}
                                        <div className="flex-shrink-0">
                                            {displayImage?.image_url ? (
                                                <img
                                                    src={getProductImageUrl(displayImage.image_url)}
                                                    alt={product.title}
                                                    className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                                                    <Package className="h-6 w-6 text-gray-400" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900">{product.title}</p>
                                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                                <span>SKU: {product.sku}</span>
                                                <span>•</span>
                                                <span>In stock: {product.available_quantity}</span>
                                                {product.selling_price && (
                                                    <>
                                                        <span>•</span>
                                                        <span>{formatPrice(product.selling_price)}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <Plus className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                                    </button>
                                );
                            })}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductSearch;